import * as React from 'react'
import clsx from 'clsx'
import { FormLabelProps, FormLabel, FormLabelClasses } from '../FormLabel'
import {
  useFormControl,
  formControlState,
  FormControlProps,
} from '../FormControl'
import style from './index.module.scss'
import { capitalize } from '../../utils'

export interface InputLabelClasses
  extends Omit<FormLabelClasses, 'ColorSecondary' | 'filled'> {
  /** Styles applied to the root element if the component is a descendant of `FormControl`. */
  formControl: string
  /** Styles applied to the root element if `variant="filled"`. */
  filled: string
  /** Styles applied to the root element if `variant="outlined"`. */
  outlined: string
  /** Styles applied to the root element if `variant="outlinedDash"`. */
  outlinedDash: string
  /** Styles applied to the root element if `variant="standard"`. */
  standard: string
}

export interface InputLabelProps extends FormLabelProps {
  /**
   * The content of the component.
   */
  children?: React.ReactNode
  /**
   * Override or extend the styles applied to the component.
   */
  classes?: Partial<InputLabelClasses>
  /**
   * The size of the component.
   * @default 'medium'
   */
  size?: 'small' | 'medium' | 'large'
  /**
   * The variant to use.
   */
  variant?: FormControlProps['variant']
}

export const InputLabel = React.forwardRef<HTMLLabelElement, InputLabelProps>(
  function InputLabel(props, ref) {
    const { classes, variant, className, ...other } = props

    const factorFormControl = useFormControl()

    const fcs = formControlState({
      props: { size: props.size, variant },
      factorFormControl,
      states: ['size', 'variant', 'required'],
    })

    const _classes = clsx(
      [style.root, classes?.root],
      factorFormControl && [style.formControl, classes?.formControl],
      // @ts-ignore
      [style[`size-${fcs.size}`], classes?.[`size${capitalize(fcs.size)}`]],
      // @ts-ignore
      fcs.variant && [style[fcs.variant], classes?.[fcs.variant]],
      fcs.required && classes?.required,
      className
    )

    return (
      <FormLabel
        ref={ref}
        className={_classes}
        {...other}
        classes={{
          ...classes,
          suffix: clsx(style.suffix, classes?.suffix),
          focused: clsx(style.focused, classes?.focused),
          disabled: classes?.disabled,
          error: clsx(style.error, classes?.error),
          asterisk: classes?.asterisk,
          filled: classes?.filled,
        }}
      />
    )
  }
)
